import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type Phaser from 'phaser';
import type { FinishPlayAttemptInput } from '../../../shared/results';
import {
  BRIDGE_GAME_FINISHED_EVENT,
  BRIDGE_HUD_UPDATE_EVENT,
  EventBus,
  type BridgeGameResult,
  type BridgeHudState,
  type BridgeHudUpdate,
} from '../../game/EventBus';
import { FullscreenGameShell } from '../../game/FullscreenGameShell';
import type { OrbitalBridgeConfig } from './domain/bridgeLevels';
import { startOrbitalBridgeGame } from './game/startOrbitalBridgeGame';

type OrbitalBridgeGameProps = {
  config: OrbitalBridgeConfig;
  label?: string;
  restartEnabled?: boolean;
  onFinished?: (result: FinishPlayAttemptInput) => void;
};

function timerLabel(remainingMs: number | null): string | null {
  if (remainingMs == null) return null;
  return `${Math.ceil(remainingMs / 1000)} sn`;
}

export function OrbitalBridgeGame({
  config,
  label = 'Yörünge Köprüsü oyun tuvali',
  restartEnabled = true,
  onFinished,
}: OrbitalBridgeGameProps) {
  const gameRef = useRef<Phaser.Game | null>(null);
  const onFinishedRef = useRef(onFinished);
  const gameId = `orbital-bridge-${useId().replace(/:/g, '')}`;
  const containerId = `${gameId}-container`;
  const [hud, setHud] = useState<BridgeHudState>({
    question: 1,
    totalQuestions: config.questions.length,
    phase: 'intro',
    mode: config.mode,
    score: 0,
    lives: 3,
    remainingMs: config.questions[0]?.timeLimitSeconds == null
      ? null
      : config.questions[0].timeLimitSeconds * 1000,
  });

  useEffect(() => { onFinishedRef.current = onFinished; }, [onFinished]);

  useLayoutEffect(() => {
    const handleHud = ({ gameId: source, hud: next }: BridgeHudUpdate) => {
      if (source === gameId) setHud(next);
    };
    const handleFinished = ({ gameId: source, result }: BridgeGameResult) => {
      if (source === gameId) onFinishedRef.current?.(result);
    };
    EventBus.on(BRIDGE_HUD_UPDATE_EVENT, handleHud);
    EventBus.on(BRIDGE_GAME_FINISHED_EVENT, handleFinished);
    gameRef.current = startOrbitalBridgeGame(containerId, gameId, config, restartEnabled);
    return () => {
      EventBus.off(BRIDGE_HUD_UPDATE_EVENT, handleHud);
      EventBus.off(BRIDGE_GAME_FINISHED_EVENT, handleFinished);
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, [config, containerId, gameId, restartEnabled]);

  const remaining = timerLabel(hud.remainingMs);
  return (
    <FullscreenGameShell
      gameRef={gameRef}
      className="orbital-bridge-shell"
      toolbar={<>
        <strong className="toolbar-level">SORU {hud.question} / {hud.totalQuestions}</strong>
        <span className="bridge-mode-label">{hud.mode === 'guided' ? 'Rehberli Keşif' : 'Üç Can Mücadelesi'}</span>
        {remaining && <strong className="bridge-timer" aria-label={`Kalan süre ${remaining}`}>{remaining}</strong>}
        <span className="toolbar-score">Puan: {hud.score}</span>
        {hud.mode === 'challenge' && (
          <span className="toolbar-lives" aria-label={`${hud.lives} can kaldı`}>
            {'♥ '.repeat(hud.lives)}{'♡ '.repeat(3 - hud.lives)}
          </span>
        )}
      </>}
    >
      <div className="game-container" id={containerId} role="application" aria-label={label} />
    </FullscreenGameShell>
  );
}
